/**
 * Datadog "Real User Monitoring" (RUM) configuration and initialization.
 * Captures live user sessions, tracks page loads, navigation, errors, resource timing, and custom actions.
 * See DATADOG.md for more information
 */
import { datadogRum } from "@datadog/browser-rum";
import { remoteDbs } from "common/constants/db";
import Flags from "common/constants/flags";

export interface InitDatadogRumOptions {
  env?: string;
  version?: string;
}

const DEFAULT_DATADOG_RUM_SITE = "us5.datadoghq.com";
const DEFAULT_DATADOG_RUM_SERVICE = "frontend";

/**
 * Use this instead of importing `RumInitConfiguration` from `@datadog/browser-rum` so typecheck does not pull the full browser-rum-core / browser-core graph.
 */
interface RumInitConfig {
  applicationId: string;
  clientToken: string;
  site: string;
  service: string;
  env: string;
  version: string;
  sessionSampleRate: number;
  sessionReplaySampleRate: number;
  traceSampleRate: number;
  telemetrySampleRate: number;
  trackUserInteractions: boolean;
  trackResources: boolean;
  trackLongTasks: boolean;
  defaultPrivacyLevel: string;
  trackViewsManually: boolean;
  allowedTracingUrls: Array<{
    match: RegExp;
    propagatorTypes: Array<"tracecontext" | "datadog">;
  }>;
}

const ALLOWED_TRACING_URLS: RumInitConfig["allowedTracingUrls"] = [
  {
    match: /https?:\/\/([a-z0-9-]+\.)*higharc\.com(?:\/|:|\?|#|$)/i,
    propagatorTypes: ["tracecontext", "datadog"],
  },
  { match: /^http:\/\/localhost/, propagatorTypes: ["tracecontext", "datadog"] },
];

function buildRumConfig(env: string, version: string): RumInitConfig {
  const applicationId = process.env.PUBLIC_DATADOG_APPLICATION_ID ?? "";
  const clientToken = process.env.PUBLIC_DATADOG_CLIENT_TOKEN ?? "";
  const site = process.env.PUBLIC_DATADOG_SITE || DEFAULT_DATADOG_RUM_SITE;
  const service = process.env.PUBLIC_DATADOG_SERVICE || DEFAULT_DATADOG_RUM_SERVICE;

  const baseConfig = {
    applicationId,
    clientToken,
    site,
    service,
    env,
    version,
    defaultPrivacyLevel: "mask-user-input",
    trackLongTasks: true,
    trackResources: true,
    trackUserInteractions: true,
    trackViewsManually: false,
    allowedTracingUrls: ALLOWED_TRACING_URLS,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    traceSampleRate: 100,
    telemetrySampleRate: 100,
  };

  return baseConfig;
}

let isRumInitialized = false;

function shouldInitializeDatadogRum(optionsEnabled: boolean): boolean {
  const override = process.env.DATADOG_ENABLED;
  if (override === "false") {
    return false;
  }
  if (override === "true") {
    return true;
  }
  return optionsEnabled;
}

export function initDatadogRum(options?: InitDatadogRumOptions): void {
  const enabled = remoteDbs.includes(Flags.DB_ENV);
  const env = options?.env ?? Flags.DB_ENV ?? "local";
  const version = options?.version ?? Flags.GIT_COMMIT_HASH ?? "unknown";

  // Skip if: already initialized, SSR, e2e, or override + caller say not to init
  if (
    isRumInitialized ||
    typeof window === "undefined" ||
    process.env.TEST_E2E_MODE === "true" ||
    !shouldInitializeDatadogRum(enabled)
  ) {
    return;
  }

  try {
    const config = buildRumConfig(env, version);
    datadogRum.init(config as Parameters<typeof datadogRum.init>[0]);
    datadogRum.setGlobalContextProperty("app", "frontend");
    datadogRum.setGlobalContextProperty("buildId", version);
    isRumInitialized = true;
    console.info("[datadog] RUM initialized successfully");
  } catch (error) {
    console.error("[datadog] Failed to initialize RUM:", error);
  }
}

/**
 * Set user information for RUM tracking
 * @param userId - Unique user identifier
 * @param userInfo - Additional user information
 */
export function setRumUser(userId: string, userInfo?: Record<string, unknown>) {
  if (!isRumInitialized) {
    return;
  }

  try {
    datadogRum.setUser({
      id: userId,
      ...userInfo,
    });
  } catch (error) {
    console.error("[datadog] Failed to set RUM user:", error);
  }
}

/**
 * Clear user information from RUM tracking
 * Should be called on logout to properly disassociate sessions from user data
 */
export function clearRumUser() {
  if (!isRumInitialized) {
    return;
  }

  try {
    datadogRum.clearUser();
  } catch (error) {
    console.error("[datadog] Failed to clear RUM user:", error);
  }
}

/**
 * Force session send for critical events
 * @param eventName - Name of the event
 * @param attributes - Additional attributes
 */
export function forceSessionSend(eventName: string, attributes?: { [key: string]: unknown }) {
  if (!isRumInitialized) {
    return;
  }

  try {
    datadogRum.addAction(eventName, {
      ...attributes,
      forced_send: true,
      timestamp: Date.now(),
    });
  } catch (error) {
    console.error("[datadog] Failed to force RUM session send:", error);
  }
}

/**
 * Add custom action to RUM
 * @param name - Action name
 * @param context - Additional context data
 */
export function addRumAction(name: string, context?: Record<string, unknown>) {
  if (!isRumInitialized) {
    return;
  }

  try {
    datadogRum.addAction(name, context);
  } catch (error) {
    console.error("[datadog] Failed to add RUM action:", error);
  }
}

/**
 * Add custom view to RUM
 * @param name - View name
 * @param context - Additional context data
 */
export function addRumView(name: string, context: Record<string, unknown>) {
  if (!isRumInitialized) {
    return;
  }

  try {
    datadogRum.startView({ name, context } as Parameters<typeof datadogRum.startView>[0]);
  } catch (error) {
    console.error("[datadog] Failed to add RUM view:", error);
  }
}

/**
 * Add custom error to RUM
 * @param error - Error object or message
 * @param context - Additional context data
 */
export function addRumError(error: Error | string, context?: Record<string, unknown>) {
  if (!isRumInitialized) {
    return;
  }

  try {
    datadogRum.addError(error, context);
  } catch (err) {
    console.error("[datadog] Failed to add RUM error:", err);
  }
}

/**
 * Track business events (custom KPIs / business metrics)
 * @param name - Event name
 * @param attributes - Additional attributes
 */
export function addRumBusinessEvent(name: string, attributes?: Record<string, unknown>) {
  if (!isRumInitialized) {
    return;
  }

  try {
    // Use addAction for business events (not feature flag evaluation)
    datadogRum.addAction(name, {
      event_type: "business_event",
      ...attributes,
    });
  } catch (error) {
    console.error("[datadog] Failed to add RUM business event:", error);
  }
}

/**
 * Add custom timing to RUM
 * @param name - Timing name
 * @param time - Time in milliseconds
 */
export function addRumTiming(name: string, time: number) {
  if (!isRumInitialized) {
    return;
  }

  try {
    datadogRum.addTiming(name, time);
  } catch (error) {
    console.error("[datadog] Failed to add RUM timing:", error);
  }
}

/**
 * Set global context for RUM
 * @param key - Context key
 * @param value - Context value
 */
export function setRumGlobalContext(key: string, value: unknown) {
  if (!isRumInitialized) {
    return;
  }

  try {
    datadogRum.setGlobalContextProperty(key, value);
  } catch (error) {
    console.error("[datadog] Failed to set RUM global context:", error);
  }
}

/**
 * Check if RUM is initialized
 * @returns True if RUM is initialized
 */
export function isRumReady() {
  return isRumInitialized;
}
